import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse } from '../models/common.model';
import { environment } from '../../../environments/environment';

export interface RouteRecord {
  id: number;
  latitude: number;
  longitude: number;
  altitude: number;
  speed: number;
  angle: number;
  satellites: number;
  battery_level: number;
  timestamp: string;
  metrics: any;
  processing?: {
    gps_quality: 'VALID' | 'SUSPECT' | 'DROP';
    movement_state: 'STATIONARY' | 'WALKING' | 'RUNNING' | 'VEHICLE' | 'DRIFT' | 'UNKNOWN';
    confidence_score: number;
    is_replay_visible: boolean;
    gps_reason: string[];
    computed_speed_kmh: number | null;
    distance_from_previous_m: number | null;
    cluster_id: string | null;
    replay_segment_type?: 'MOVING_SEGMENT' | 'STATIONARY_CLUSTER' | 'SPIKE' | 'GAP';
    replay_segment_id?: string | null;
  };
}

export type TeltonikaAction =
  | 'play_alert'
  | 'stop_alert'
  | 'get_status'
  | 'get_gps'
  | 'get_battery'
  | 'get_info'
  | 'get_version'
  | 'request_location_record'
  | 'count_records'
  | 'get_remote_indication_config'
  | 'reboot_device';


@Injectable({
  providedIn: 'root',
})
export class TeltonikaService {
  private readonly baseUrl = `${environment.apiUrl}/teltonika`;

  constructor(private http: HttpClient) {}

  /**
   * Retorna os aparelhos activos da empresa com as suas últimas posições
   */
  getDevices(companyId?: number): Observable<ApiResponse<any[]>> {
    const cid = companyId || 1;
    return this.http.get<ApiResponse<any[]>>(`${this.baseUrl}/devices`, {
      params: { company_id: String(cid) }
    });
  }

  /**
   * Retorna o histórico cronológico de coordenadas de um aparelho para desenhar a rota
   */
  getRouteHistory(deviceId: number, from: string, to: string): Observable<ApiResponse<RouteRecord[]>> {
    return this.http.get<ApiResponse<RouteRecord[]>>(`${this.baseUrl}/devices/${deviceId}/route-history`, {
      params: { from, to },
    });
  }

  /**
   * Envia um comando Codec 12 activo por TCP ao rastreador
   */
  sendCommand(deviceId: number, command: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.baseUrl}/devices/${deviceId}/command`, { command });
  }

  sendAction(
    deviceId: number,
    action: TeltonikaAction,
    params?: Record<string, any>
  ): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/devices/${deviceId}/actions`, {
      action,
      params,
    });
  }

  /**
   * Salva/Atualiza as configurações do chip SIM do rastreador
   */
  configureDevice(deviceId: number, phoneNumber: string, authorizedNumbers: string[]): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/devices/${deviceId}/config`, {
      phone_number: phoneNumber,
      authorized_numbers: authorizedNumbers,
    });
  }

  /**
   * Retorna os alertas do dispositivo com paginação e filtro por tipo
   */
  getDeviceAlerts(deviceId: number, limit = 10, offset = 0, type = 'ALL'): Observable<ApiResponse<any>> {
    const params: Record<string, string> = {
      limit: String(limit),
      offset: String(offset),
    };
    if (type !== 'ALL') params['type'] = type;
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/devices/${deviceId}/alerts`, { params });
  }

  /**
   * Retorna as últimas mensagens/telemetrias recebidas deste dispositivo (com paginação)
   */
  getDeviceTelemetries(deviceId: number, limit = 50, offset = 0): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/devices/${deviceId}/telemetries`, {
      params: { limit: String(limit), offset: String(offset) },
    });
  }
}
